import React, { useState } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from '@xyflow/react';
import { ListTree, X } from 'lucide-react';
import { SwitchNodeData, getSwitchCaseHandle, SWITCH_HANDLE_DEFAULT } from '../types';

export function SwitchNode({ id, data: rawData, selected }: NodeProps) {
  const data = rawData as unknown as SwitchNodeData;
  const cases = data.cases || [];
  const [isHovered, setIsHovered] = useState(false);
  const { setNodes, setEdges } = useReactFlow();

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes((nodes) => nodes.filter((n) => n.id !== id));
    setEdges((edges) => edges.filter((e) => e.source !== id && e.target !== id));
  };

  return (
    <div
      className={`relative min-w-[220px] rounded-xl border-2 bg-white shadow-lg transition-all ${
        selected ? 'border-purple-500 shadow-purple-200' : 'border-purple-400'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Delete button on hover */}
      {isHovered && (
        <button
          onClick={handleDelete}
          className="absolute -top-2 -right-2 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow-md hover:bg-red-600 transition-colors"
          title="Delete node"
        >
          <X size={12} />
        </button>
      )}
      <Handle type="target" position={Position.Top} className="!bg-purple-500 !w-3 !h-3" />
      <div className="flex items-center gap-2 rounded-t-xl bg-purple-500 px-3 py-2">
        <ListTree size={14} className="text-white" />
        <span className="text-xs font-semibold uppercase tracking-wider text-white">Switch</span>
      </div>
      <div className="px-3 py-2">
        <p className="text-xs text-gray-500 dark:text-gray-400">Field</p>
        <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
          {data.displayLabel || data.field || 'No field selected'}
        </p>
      </div>
      {/* Dynamic output handles for each case */}
      <div className="flex flex-wrap justify-center gap-3 px-3 py-2 border-t border-purple-200 bg-purple-50 rounded-b-xl">
        {cases.map((caseItem, index) => (
          <div key={index} className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-blue-600 mb-1 truncate max-w-[60px]">
              {caseItem.label || String(caseItem.value)}
            </span>
            <Handle
              type="source"
              position={Position.Bottom}
              id={getSwitchCaseHandle(index)}
              className="!bg-blue-500 !w-3 !h-3 !relative !transform-none !left-0 !right-auto !bottom-0"
              style={{ position: 'relative' }}
            />
          </div>
        ))}
        {data.hasDefault && (
          <div className="flex flex-col items-center">
            <span className="text-[10px] font-semibold text-gray-600 mb-1">Default</span>
            <Handle
              type="source"
              position={Position.Bottom}
              id={SWITCH_HANDLE_DEFAULT}
              className="!bg-gray-500 !w-3 !h-3 !relative !transform-none !left-0 !right-auto !bottom-0"
              style={{ position: 'relative' }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
